import { Link } from "react-router-dom";
import { Flower, ArrowLeft, Download, EnvelopeSimple, Quotes, Sparkle, Copyright, ShieldCheck } from "@phosphor-icons/react";
import SiteFooter from "@/components/SiteFooter";
import usePageMeta from "@/lib/usePageMeta";

const FACTS = [
  { k: "Founded", v: "2021" },
  { k: "Headquarters", v: "United States" },
  { k: "Founder", v: "Adonica Shaw" },
  { k: "Investors", v: "StartUp Health (2022), Jumpstart Foundry (2023)" },
  { k: "Category", v: "Digital health · Peer support" },
  { k: "AI model", v: "Anthropic Claude Sonnet 4.5" },
  { k: "Privacy", v: "HIPAA-aligned architecture" },
  { k: "Community", v: "Anonymous by default" },
  { k: "Conditions", v: "Endometriosis, PMOS, IVF, Pregnancy, Pregnancy loss, Postpartum, Fibroids, Menopause" },
];

const QUOTES = [
  {
    q: "No woman should have to navigate her reproductive health journey alone.",
    a: "Adonica Shaw, Founder",
  },
  {
    q: "Lived experience is medicine. Wingwomen is where women who've walked the path meet the women just beginning it.",
    a: "Wingwomen mission",
  },
];

export default function Press() {
  usePageMeta({
    title: "Press Kit",
    description: "Wingwomen press kit, founder story, logo assets, quick facts, and press contact.",
  });
  return (
    <div className="min-h-screen bg-cream text-ink">
      <header className="glass sticky top-0 z-40">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
          <Link to="/" data-testid="press-brand-link" className="flex items-center gap-2">
            <Flower size={22} weight="duotone" className="text-coral-700" />
            <span className="font-display text-xl">Wingwomen</span>
          </Link>
          <Link to="/" className="text-sm hover:text-coral-700 flex items-center gap-1.5">
            <ArrowLeft size={14} /> Back
          </Link>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-16">
        <div className="text-xs tracking-[0.25em] uppercase text-sage-600 mb-4"><span className="rule" />For journalists & partners</div>
        <h1 className="font-display text-5xl sm:text-6xl leading-tight" data-testid="press-title">
          Press kit.
        </h1>
        <p className="mt-5 text-ink/75 max-w-2xl leading-relaxed">
          Everything you need to write about Wingwomen, the founder's story, brand assets,
          quick facts, and a direct line to us. If something's missing, email{" "}
          <a href="mailto:hi@mywingwomen.com" data-testid="press-contact-inline" className="text-coral-700 underline underline-offset-4">
            hi@mywingwomen.com
          </a>{" "}
          and we'll get it to you.
        </p>

        {/* Pull quotes */}
        <div className="mt-12 grid md:grid-cols-2 gap-4">
          {QUOTES.map((q, i) => (
            <blockquote key={i} className="relative bg-white border border-border rounded-2xl p-6 shadow-soft">
              <Quotes size={22} weight="fill" className="text-coral/40 absolute top-4 right-4" />
              <p className="font-display text-lg leading-snug pr-6">"{q.q}"</p>
              <div className="text-xs tracking-[0.15em] uppercase text-muted-foreground mt-3">{q.a}</div>
            </blockquote>
          ))}
        </div>

        {/* Founder story */}
        <section id="founder" className="mt-16 scroll-mt-24">
          <div className="text-xs tracking-[0.25em] uppercase text-sage-600 mb-4"><span className="rule" />A note from our founder</div>
          <h2 className="font-display text-3xl sm:text-4xl leading-tight">Why Wingwomen.</h2>
          <div className="mt-6 grid md:grid-cols-12 gap-6 items-start">
            <div className="md:col-span-8 space-y-4 text-ink/85 leading-relaxed">
              <p>
                After the birth of my son, I found myself struggling with postpartum challenges
                in ways I never expected. I was well established in my career, but I didn't have
                a community I could turn to or a safe place to admit I was having a hard time.
                Like so many women, I felt isolated, and I worried about protecting my privacy
                and professional identity while seeking support.
              </p>
              <p>
                What changed everything was realizing how many other women were carrying the same
                struggles in silence.
              </p>
              <p>
                In 2021, I started imagining a different kind of support system, one where
                women could connect anonymously with others who truly understood their
                reproductive health journey, whether that was IVF, PMOS, endometriosis,
                pregnancy, postpartum recovery, or pregnancy loss.
              </p>
              <p>
                That's why Wingwomen exists. Every match, every conversation, and every AI-guided
                connection is built around one belief: <em className="text-coral-700 not-italic font-medium">no
                woman should have to navigate her reproductive health journey alone.</em>
              </p>
              <p className="text-sm text-sage-700 pt-2 tracking-wide">Adonica Shaw, Founder</p>
            </div>
            <div className="md:col-span-4">
              <a href="/press/founder-note.txt" download
                 data-testid="press-download-note"
                 className="flex items-center justify-between bg-white border border-border rounded-xl px-4 py-3 text-sm hover:border-coral hover:text-coral-700 transition-colors">
                <span>Download founder note (.txt)</span>
                <Download size={16} weight="duotone" />
              </a>
            </div>
          </div>
        </section>

        {/* Quick facts */}
        <section id="facts" className="mt-16 scroll-mt-24">
          <div className="text-xs tracking-[0.25em] uppercase text-sage-600 mb-4"><span className="rule" />Quick facts</div>
          <h2 className="font-display text-3xl sm:text-4xl leading-tight">Everything at a glance.</h2>
          <dl className="mt-8 grid sm:grid-cols-2 gap-x-8 gap-y-4 bg-white border border-border rounded-2xl p-6 shadow-soft" data-testid="press-facts">
            {FACTS.map(({ k, v }) => (
              <div key={k} className="flex flex-col border-b border-border/60 last:border-0 sm:[&:nth-last-child(2)]:border-0 pb-3">
                <dt className="text-[10px] tracking-[0.2em] uppercase text-muted-foreground">{k}</dt>
                <dd className="font-display text-lg mt-1">{v}</dd>
              </div>
            ))}
          </dl>
        </section>

        {/* Logos */}
        <section id="logos" className="mt-16 scroll-mt-24">
          <div className="text-xs tracking-[0.25em] uppercase text-sage-600 mb-4"><span className="rule" />Brand assets</div>
          <h2 className="font-display text-3xl sm:text-4xl leading-tight">Logo & wordmark.</h2>
          <p className="mt-3 text-sm text-ink/70 max-w-xl leading-relaxed">
            Use the logo on cream or white surfaces (Light) and on dark/ink backgrounds (Dark).
            Preserve at least the logo's own height as clear space around it. Please don't
            recolor, rotate, or stretch it.
          </p>

          <div className="mt-8 grid md:grid-cols-2 gap-5">
            <div className="bg-white border border-border rounded-2xl overflow-hidden shadow-soft">
              <div className="bg-cream p-8 grid place-items-center">
                <img src="/press/wingwomen-logo.svg" alt="Wingwomen logo, light"
                     className="max-w-full h-auto" data-testid="logo-light-preview" />
              </div>
              <div className="p-4 flex items-center justify-between border-t border-border">
                <div>
                  <div className="font-display text-base">Light, SVG</div>
                  <div className="text-xs text-muted-foreground">Vector · scales without loss</div>
                </div>
                <a href="/press/wingwomen-logo.svg" download="wingwomen-ai-logo.svg"
                   data-testid="download-logo-light"
                   className="inline-flex items-center gap-1.5 bg-ink text-cream text-xs px-3 py-2 rounded-full hover:bg-coral transition-colors">
                  <Download size={12} weight="duotone" /> Download
                </a>
              </div>
            </div>

            <div className="bg-ink border border-ink rounded-2xl overflow-hidden shadow-soft">
              <div className="bg-ink p-8 grid place-items-center">
                <img src="/press/wingwomen-logo-dark.svg" alt="Wingwomen logo, dark"
                     className="max-w-full h-auto" data-testid="logo-dark-preview" />
              </div>
              <div className="p-4 flex items-center justify-between border-t border-cream/10 bg-cream">
                <div>
                  <div className="font-display text-base">Dark, SVG</div>
                  <div className="text-xs text-muted-foreground">For dark surfaces</div>
                </div>
                <a href="/press/wingwomen-logo-dark.svg" download="wingwomen-ai-logo-dark.svg"
                   data-testid="download-logo-dark"
                   className="inline-flex items-center gap-1.5 bg-ink text-cream text-xs px-3 py-2 rounded-full hover:bg-coral transition-colors">
                  <Download size={12} weight="duotone" /> Download
                </a>
              </div>
            </div>
          </div>

          <div className="mt-6 p-4 bg-sage-50 border border-sage-100 rounded-xl text-xs text-ink/75 leading-relaxed">
            <Sparkle size={12} weight="duotone" className="inline mr-1.5 text-sage" />
            Need PNG, EPS, or a specific size? Email us and we'll send it same-day.
          </div>

          {/* Historic wordmark */}
          <div className="mt-10">
            <div className="text-xs tracking-[0.2em] uppercase text-sage-600 mb-3">
              <span className="rule" />Historic wordmark
            </div>
            <div className="bg-white border border-border rounded-2xl overflow-hidden shadow-soft">
              <div className="bg-white p-10 grid place-items-center">
                <img
                  src="/press/wingwomen-logo-historic.webp"
                  alt="Wingwomen historic wordmark, set in Morgan"
                  data-testid="logo-historic-preview"
                  className="max-w-full h-auto"
                />
              </div>
              <div className="p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 border-t border-border">
                <div>
                  <div className="font-display text-base">Historic wordmark</div>
                  <div className="text-xs text-muted-foreground">
                    Original Wingwomen wordmark, set in the <em>Morgan</em> typeface. Used from
                    2021 through early 2024. Available for archival, editorial, and historical
                    coverage only.
                  </div>
                </div>
                <a
                  href="/press/wingwomen-logo-historic.png"
                  download="wingwomen-historic-wordmark.png"
                  data-testid="download-logo-historic"
                  className="shrink-0 inline-flex items-center gap-1.5 bg-ink text-cream text-xs px-3 py-2 rounded-full hover:bg-coral transition-colors"
                >
                  <Download size={12} weight="duotone" /> Download PNG
                </a>
              </div>
            </div>
            <p className="mt-3 text-[11px] text-muted-foreground leading-relaxed">
              <strong>Typography note:</strong> the historic wordmark is set in{" "}
              <em>Morgan</em>, a licensed typeface by its respective foundry. The wordmark shown
              is retained as a raster asset for archival reference; the Morgan typeface itself is
              not distributed with this press kit. For high-resolution assets or vector versions,
              email{" "}
              <a href="mailto:hi@mywingwomen.com" className="text-coral-700 underline underline-offset-4">
                hi@mywingwomen.com
              </a>.
            </p>
          </div>
        </section>

        {/* Ownership & IP Disclosure */}
        <section id="ownership" className="mt-16 scroll-mt-24" data-testid="press-ownership">
          <div className="text-xs tracking-[0.25em] uppercase text-sage-600 mb-4">
            <span className="rule" />Ownership & intellectual property
          </div>
          <div className="bg-white border border-border rounded-2xl p-8 shadow-card space-y-5 text-sm text-ink/80 leading-relaxed">
            <div className="flex items-start gap-3">
              <Copyright size={18} weight="duotone" className="text-coral-700 shrink-0 mt-0.5" />
              <p>
                Wingwomen is a product of <strong>Wingwomen, Inc.</strong>, founded in 2021.
                The company is venture-backed, having received pre-seed investment from{" "}
                <strong>StartUp Health</strong> in 2022 and the{" "}
                <strong>Jumpstart Foundry</strong> accelerator in 2023. All references to
                "Wingwomen", "Wingwomen", or the platform on this site refer to Wingwomen, Inc.
              </p>
            </div>

            <div className="flex items-start gap-3">
              <ShieldCheck size={18} weight="duotone" className="text-coral-700 shrink-0 mt-0.5" />
              <p>
                The Wingwomen brand, name, logo, tagline, editorial voice, similarity-score
                matching algorithm, condition hub taxonomy, loss-aware AI system prompts, and all
                associated product concepts and code are the proprietary intellectual property of
                Wingwomen, Inc. and its licensors.
              </p>
            </div>

            <div className="flex items-start gap-3">
              <Sparkle size={18} weight="duotone" className="text-coral-700 shrink-0 mt-0.5" />
              <p>
                <strong>Wingwomen™</strong>, <strong>Wingwomen™</strong>, and the flower mark
                are trademarks of Wingwomen, Inc. Third-party names, logos, and marks (Anthropic,
                Claude, ElevenLabs, Stripe, Twilio, others) are the property of their respective
                owners and are used here for identification only, no endorsement is implied.
              </p>
            </div>

            <div className="pt-4 mt-4 border-t border-border">
              <div className="text-[11px] tracking-[0.2em] uppercase text-sage-600 mb-2">
                Permitted use for press
              </div>
              <p className="text-xs text-ink/70">
                Journalists and analysts may reproduce the Wingwomen name, logo, product
                screenshots, founder quotes, and quick facts on this page for editorial coverage.
                Any other use, including licensing, partnership, adaptation of the matching
                methodology, product concepts, or condition hub taxonomy, requires prior written
                permission from Wingwomen, Inc. Email{" "}
                <a href="mailto:hi@mywingwomen.com" className="text-coral-700 underline underline-offset-4">
                  hi@mywingwomen.com
                </a>{" "}
                for licensing or partnership inquiries.
              </p>
            </div>

            <p className="text-[11px] text-muted-foreground pt-2">
              © 2021–2026 Wingwomen, Inc. All rights reserved.
            </p>
          </div>
        </section>

        {/* Contact */}
        <section id="contact" className="mt-16 scroll-mt-24">
          <div className="text-xs tracking-[0.25em] uppercase text-sage-600 mb-4"><span className="rule" />Press contact</div>
          <div className="bg-white border border-border rounded-2xl p-8 shadow-card">
            <div className="text-xs tracking-[0.2em] uppercase text-sage-600 mb-2 flex items-center gap-1.5">
              <EnvelopeSimple size={14} weight="duotone" /> For interviews, features, and quotes
            </div>
            <a href="mailto:hi@mywingwomen.com?subject=Press%20inquiry"
               data-testid="press-mailto"
               className="font-display text-2xl sm:text-3xl leading-tight text-coral-700 hover:text-coral-600 underline decoration-coral/40 underline-offset-8">
              hi@mywingwomen.com
            </a>
            <p className="mt-4 text-sm text-ink/70">
              We reply to press inquiries within one business day.
            </p>
          </div>
        </section>
      </main>

      <SiteFooter compact />
    </div>
  );
}
